/**
 * The one place the app asks "am I inside the Android shell?".
 *
 * `@capacitor/core` is the only Capacitor import allowed at module scope in
 * this directory: it is small, it is what every plugin proxy is built on, and
 * in a plain browser it answers `web` without touching anything native. Every
 * plugin is imported INSIDE the function that uses it, so the web bundle pulls
 * a plugin's chunk only when a caller actually reaches for it.
 */
import { Capacitor, type PluginListenerHandle } from "@capacitor/core";

export type NativePlatform = "android" | "ios" | "web";

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

export function getPlatform(): NativePlatform {
  const platform = Capacitor.getPlatform();
  return platform === "android" || platform === "ios" ? platform : "web";
}

/** Stops a subscription. Calling it twice is harmless. */
export type Unsubscribe = () => void;

/**
 * Turns Capacitor's `Promise<PluginListenerHandle>` into a synchronous
 * unsubscribe, which is what a React effect cleanup needs. If the cleanup runs
 * before the registration resolves, the handle is removed as soon as it
 * arrives rather than leaking. A registration that rejects is logged and
 * otherwise ignored: a missing listener must not take the screen down.
 */
export function toUnsubscribe(
  registration: Promise<PluginListenerHandle>
): Unsubscribe {
  let handle: PluginListenerHandle | null = null;
  let cancelled = false;
  registration
    .then((resolved) => {
      if (!cancelled) {
        handle = resolved;
        return;
      }
      return resolved.remove();
    })
    .catch((error: unknown) => {
      console.warn("[native] listener registration failed", error);
    });
  return () => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    handle?.remove().catch(() => undefined);
    handle = null;
  };
}

/** The unsubscribe of a subscription that never existed (web no-ops). */
export const noopUnsubscribe: Unsubscribe = () => undefined;
